import { useEffect, useMemo, useState } from 'react';
import {
  DICE_CATALOG,
  type CosmeticKey,
  type EffectPresentation,
  type GameAction,
  type LegalMove,
  type RoomView,
} from '@shared/game/types';
import { cellAt, trackIndexForProgress } from '@shared/game/board';
import { EFFECT_PREVIEW_COPY } from '../lib/effectCopy';
import type { Preferences } from '../lib/preferences';
import { moveDetails, moveLabel } from '../lib/format';
import { Avatar } from './Avatar';
import { Board } from './Board';
import { Brand } from './Brand';
import { Confetti } from './Confetti';
import { Dice } from './Dice';
import { EffectCinematic } from './EffectCinematic';
import { Icon, type IconName } from './Icon';
import { LogPanel } from './LogPanel';
import { Modal } from './Modal';
import { PlayersPanel } from './PlayersPanel';

interface GameScreenProps {
  room: RoomView;
  playerId: string;
  preferences: Preferences;
  cosmetics: CosmeticKey[];
  action: GameAction | null;
  busy: boolean;
  connected: boolean;
  onRoll: () => void;
  onMove: (pieceId: string) => void;
  onForfeit: () => void;
  onRematch: () => void;
  onRules: () => void;
  onSettings: () => void;
  onShop: () => void;
  onCopyCode: () => void;
  onToggleSound: () => void;
  onToggleMusic: () => void;
}

const EFFECT_ICONS: Record<string, IconName> = {
  shortcut: 'portal',
  risk: 'rift',
  event: 'spark',
  advance: 'wind',
  retreat: 'fog',
  shield: 'shield',
  wait: 'moon',
  extraTurn: 'spark',
  capture: 'capture',
  skip: 'moon',
  penalty: 'six',
  win: 'crown',
  extra: 'spark',
};

export function GameScreen({
  room,
  playerId,
  preferences,
  cosmetics,
  action,
  busy,
  connected,
  onRoll,
  onMove,
  onForfeit,
  onRematch,
  onRules,
  onSettings,
  onShop,
  onCopyCode,
  onToggleSound,
  onToggleMusic,
}: GameScreenProps) {
  const game = room.game;
  const [effect, setEffect] = useState<EffectPresentation | null>(null);
  const [effectKey, setEffectKey] = useState(0);
  const [forfeitOpen, setForfeitOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<LegalMove | null>(null);
  const [movingPieceId, setMovingPieceId] = useState<string | null>(null);
  const [safePieceId, setSafePieceId] = useState<string | null>(null);

  useEffect(() => {
    if (action?.type !== 'move' || !action.pieceId) return undefined;
    setMovingPieceId(action.pieceId);
    const timeout = window.setTimeout(() => setMovingPieceId(null), 680);
    return () => window.clearTimeout(timeout);
  }, [action]);

  useEffect(() => {
    if (action?.type !== 'move' || !action.pieceId || !game) return undefined;
    const player = game.players.find((candidate) => candidate.id === action.playerId);
    const piece = player?.pieces.find((candidate) => candidate.id === action.pieceId);
    if (!player || !piece || piece.status !== 'active') return undefined;
    const track = trackIndexForProgress(player.color, action.animation?.to ?? piece.progress);
    const cell = track === null ? null : cellAt(track);
    if (!cell || (cell.kind !== 'safe' && cell.kind !== 'start')) return undefined;
    setSafePieceId(action.pieceId);
    const timeout = window.setTimeout(() => setSafePieceId(null), 950);
    return () => window.clearTimeout(timeout);
  }, [action, game]);

  const current = game?.players.find((player) => player.id === game.currentPlayerId);
  const myPlayer = game?.players.find((player) => player.id === playerId);
  const winner = game?.winnerId ? game.players.find((player) => player.id === game.winnerId) : null;
  const isHost = room.hostId === playerId;
  const myTurn = Boolean(game && current?.id === playerId && game.phase !== 'finished');
  const effectsActive = Boolean(game?.mode === 'adventure' && game.effectsEnabled);
  const isRoll = action?.type === 'roll';

  useEffect(() => {
    if (!action?.effect) return undefined;
    setEffect(action.effect);
    setEffectKey((key) => key + 1);
    const timeout = window.setTimeout(() => setEffect(null), 4400);
    return () => window.clearTimeout(timeout);
  }, [action]);

  const activeDiceSkin = useMemo(() => {
    if (current?.diceSkin) return current.diceSkin;
    return preferences.diceSkin;
  }, [current?.diceSkin, preferences.diceSkin]);

  const diceValue = game?.dice ?? action?.dice ?? null;
  const rollKey = isRoll ? `${action.id}-${action.dice}` : undefined;
  const myMoves = useMemo(() => {
    if (!game || !myTurn) return [];
    return game.legalMoves;
  }, [game, myTurn]);

  const handleMove = (pieceId: string) => {
    const move = myMoves.find((candidate) => candidate.pieceId === pieceId);
    if (!move) return;
    if (move.effectPreview) {
      setPendingMove(move);
      return;
    }
    onMove(pieceId);
  };

  if (!game || !myPlayer) {
    return (
      <div className="app app--game">
        <div className="loading-screen">
          <span className="spinner" />
          <p>Recuperando la partida…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`app app--game ${preferences.animations ? '' : 'no-animations'}`}>
      <header className="game-topbar">
        <Brand compact />
        <div className="game-topbar__center">
          <span className={`mode-badge mode-badge--${game.mode}`}>
            <Icon name={game.mode === 'adventure' ? 'spark' : 'sword'} size={13} />
            {game.mode === 'adventure' ? 'Aventura' : 'Clásico'}
          </span>
          <button type="button" className="room-code room-code--button" onClick={onCopyCode} title="Copiar código">
            {room.code} <Icon name="copy" size={13} />
          </button>
          <span className={`effect-pill ${effectsActive ? 'is-on' : ''}`}>
            <Icon name={effectsActive ? 'spark' : 'shield'} size={13} />
            Anomalías {effectsActive ? 'ON' : 'OFF'}
          </span>
        </div>
        <div className="game-topbar__actions">
          <button type="button" className="icon-button" onClick={onRules} aria-label="Reglas">
            <Icon name="book" size={18} />
          </button>
          <button type="button" className="icon-button" onClick={onShop} aria-label="Cosméticos">
            <Icon name="bag" size={18} />
          </button>
          <button type="button" className="icon-button" onClick={onToggleSound} aria-label={preferences.sound ? 'Silenciar efectos' : 'Activar efectos'}>
            <Icon name={preferences.sound ? 'volume' : 'volume-off'} size={18} />
          </button>
          <button type="button" className={`icon-button ${preferences.music ? 'is-active' : ''}`} onClick={onToggleMusic} aria-label={preferences.music ? 'Desactivar música' : 'Activar música'}>
            <Icon name="music" size={18} />
          </button>
          <button type="button" className="icon-button" onClick={onSettings} aria-label="Ajustes">
            <Icon name="settings" size={18} />
          </button>
          <button type="button" className="button button--ghost button--small" onClick={() => setForfeitOpen(true)}>
            <Icon name="logout" size={15} /> Salir
          </button>
        </div>
      </header>

      {!connected && (
        <div className="reconnect-banner">
          <span className="spinner spinner--small" />
          Conexión perdida. Intentando reconectar; tu asiento está reservado.
        </div>
      )}

      <main className="game-layout">
        <section className="game-board-column">
          <div className="game-strip">
            <div className="game-strip__turn">
              <span className={`turn-orb ${myTurn ? 'is-mine' : ''}`}>
                <Icon name={myTurn ? 'play' : 'clock'} size={14} />
              </span>
              <span>
                {game.phase === 'finished'
                  ? 'Partida terminada'
                  : myTurn
                    ? 'Tu turno'
                    : `Turno de ${current?.name ?? '…'}`}
              </span>
              <span className="game-strip__divider" />
              <span className="muted small">Ciclo {game.turnNumber}</span>
              <span className="chapter-tag">Protocolo Núcleo · Sector 04</span>
            </div>
            <div className="game-strip__players">
              {game.players.map((player) => (
                <span key={player.id} className={`mini-player mini-player--${player.color} ${player.id === game.currentPlayerId ? 'is-current' : ''}`} title={player.name}>
                  <Avatar avatar={player.avatar} size={25} />
                  <span>{player.name}</span>
                </span>
              ))}
            </div>
          </div>

          <div className={`turn-callout ${myTurn ? 'is-mine' : ''} ${game.phase === 'finished' ? 'is-finished' : ''}`}>
            <span className="turn-callout__sigil">
              <Icon name={game.phase === 'finished' ? 'crown' : myTurn ? 'play' : 'clock'} size={18} />
            </span>
            <span className="turn-callout__copy">
              <strong>{game.phase === 'finished' ? 'ARENA CERRADA' : myTurn ? 'TU TURNO' : `TURNO DE ${current?.name?.toUpperCase() ?? '…'}`}</strong>
              <small>{myTurn ? 'Lanza el dado y elige tu unidad' : game.phase === 'finished' ? 'Consulta el resultado' : 'La arena está en movimiento'}</small>
            </span>
            <span className="turn-callout__cycle">Ciclo {game.turnNumber}</span>
          </div>

          <div className="game-board-wrap">
            <Board
              game={game}
              myPlayerId={playerId}
              onMove={handleMove}
              animations={preferences.animations}
              movingPieceId={movingPieceId}
              safePieceId={safePieceId}
            />
            <EffectCinematic
              action={action}
              game={game}
              enabled={preferences.animations}
              astral={cosmetics.includes('capture-effect-astral')}
            />

            {isRoll && (
              <div className={`board-dice ${preferences.animations ? 'is-falling' : ''}`} aria-label={`Dado sobre el tablero: ${diceValue ?? ''}`}>
                <span className="board-dice__tray" aria-hidden="true" />
                <span className="board-dice__wood" aria-hidden="true" />
                <Dice
                  value={diceValue}
                  skin={activeDiceSkin}
                  rollKey={rollKey}
                  animations={preferences.animations}
                  size="normal"
                  effect={cosmetics.includes('dice-effect-astral') ? 'astral' : undefined}
                />
              </div>
            )}

            {effect && (
              <div key={effectKey} className={`effect-toast effect-toast--${effect.kind}`} role="status">
                <span className="effect-toast__icon">
                  <Icon name={EFFECT_ICONS[effect.kind] ?? 'spark'} size={25} />
                </span>
                <div>
                  <span className="eyebrow">Anomalía detectada</span>
                  <strong>{effect.title}</strong>
                  <p>{effect.description}</p>
                  {effect.from !== undefined && effect.to !== undefined && (
                    <span className="effect-toast__delta">
                      Recorrido {effect.from} → {effect.to}
                    </span>
                  )}
                </div>
                <button type="button" className="icon-button icon-button--small" onClick={() => setEffect(null)} aria-label="Cerrar efecto">
                  <Icon name="close" size={14} />
                </button>
              </div>
            )}

            {game.phase === 'finished' && winner && (
              <div className="winner-overlay">
                <Confetti active={preferences.animations} />
                <div className="winner-card">
                  <span className="winner-card__crown"><Icon name="crown" size={30} /></span>
                  <span className="eyebrow">La meta tiene dueño</span>
                  <div className={`winner-card__avatar winner-card__avatar--${winner.color}`}>
                    <Avatar avatar={winner.avatar} size={104} />
                  </div>
                  <h2>{winner.id === playerId ? '¡Has ganado!' : `${winner.name} gana`}</h2>
                  <p>
                    {winner.id === playerId
                      ? 'Cuatro unidades en el Núcleo. El cielo de Altia lleva tu color.'
                      : `${winner.name} ha encendido el Núcleo de Altia antes que nadie.`}
                  </p>
                  <div className="winner-card__actions">
                    {isHost ? (
                      <button type="button" className="button button--primary" onClick={onRematch} disabled={busy}>
                        <Icon name="refresh" size={17} /> Revancha
                      </button>
                    ) : (
                      <span className="waiting-box waiting-box--inline"><span className="waiting-dots"><i /><i /><i /></span> Esperando revancha del anfitrión</span>
                    )}
                    <button type="button" className="button button--ghost" onClick={() => setForfeitOpen(true)}>Volver al inicio</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mobile-play-panel">
            <TurnPanel
              game={game}
              currentName={current?.name}
              myTurn={myTurn}
              myMoves={myMoves}
              busy={busy}
              diceValue={diceValue}
              activeDiceSkin={activeDiceSkin}
              rollKey={rollKey}
              animations={preferences.animations}
              astralDice={cosmetics.includes('dice-effect-astral')}
              profileFrame={cosmetics.includes('profile-frame-astral')}
              action={action}
              onRoll={onRoll}
              onMove={handleMove}
              myPlayerName={myPlayer.name}
            />
            <PlayersPanel game={game} playerId={playerId} />
          </div>
        </section>

        <aside className="game-sidebar">
          <TurnPanel
            game={game}
            currentName={current?.name}
            myTurn={myTurn}
            myMoves={myMoves}
            busy={busy}
            diceValue={diceValue}
            activeDiceSkin={activeDiceSkin}
            rollKey={rollKey}
            animations={preferences.animations}
            astralDice={cosmetics.includes('dice-effect-astral')}
            profileFrame={cosmetics.includes('profile-frame-astral')}
            action={action}
            onRoll={onRoll}
            onMove={handleMove}
            myPlayerName={myPlayer.name}
          />
          <PlayersPanel game={game} playerId={playerId} />
          <LogPanel log={game.log} />
          <div className="game-sidebar__footer">
            <button type="button" className="button button--ghost button--small" onClick={onRules}><Icon name="book" size={15} /> Reglas</button>
            <button type="button" className="button button--ghost button--small" onClick={onSettings}><Icon name="settings" size={15} /> Ajustes</button>
          </div>
        </aside>
      </main>

      <Modal
        open={Boolean(pendingMove)}
        onClose={() => setPendingMove(null)}
        title={pendingMove?.effectPreview?.title ?? 'Anomalía detectada'}
        subtitle="La unidad va a entrar en una celda inestable. Lee la regla antes de confirmar."
        footer={
          <>
            <button type="button" className="button button--ghost" onClick={() => setPendingMove(null)}>
              Elegir otra unidad
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                if (pendingMove) onMove(pendingMove.pieceId);
                setPendingMove(null);
              }}
            >
              <Icon name="play" size={17} /> Confirmar salto
            </button>
          </>
        }
      >
        {pendingMove?.effectPreview && (
          <div className={`effect-preview-card effect-preview-card--${pendingMove.effectPreview.kind}`}>
            <span className="effect-preview-card__icon">
              <Icon name={EFFECT_ICONS[pendingMove.effectPreview.kind] ?? 'spark'} size={26} />
            </span>
            <div>
              <span className="eyebrow">Antes de aplicar</span>
              <h3>{pendingMove.effectPreview.title}</h3>
              <p>{pendingMove.effectPreview.description}</p>
              <small>
                {pendingMove.effectPreview.kind === 'event'
                  ? 'El resultado exacto de este evento es secreto hasta que el servidor lo resuelva. Se mostrará con una animación y en el registro.'
                  : EFFECT_PREVIEW_COPY[pendingMove.effectPreview.kind]}
              </small>
              {pendingMove.captures > 0 && (
                <div className="notice notice--warning">
                  <Icon name="capture" size={15} /> Este movimiento también captura {pendingMove.captures} ficha
                  {pendingMove.captures === 1 ? '' : 's'} antes de resolver el efecto.
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={forfeitOpen}
        onClose={() => setForfeitOpen(false)}
        title={game.phase === 'finished' ? 'Volver al inicio' : 'Abandonar la partida'}
        subtitle={game.phase === 'finished' ? 'La partida ya terminó. Tu asiento se liberará.' : 'Esta acción no se puede deshacer.'}
        footer={
          <>
            <button type="button" className="button button--ghost" onClick={() => setForfeitOpen(false)}>Quedarme</button>
            <button type="button" className="button button--danger" onClick={onForfeit}>
              <Icon name="logout" size={16} /> {game.phase === 'finished' ? 'Salir' : 'Abandonar'}
            </button>
          </>
        }
      >
        {game.phase === 'finished' ? (
          <p>La partida ha terminado. Puedes volver al vestíbulo y crear otra mesa.</p>
        ) : (
          <div className="forfeit-copy">
            <p>
              Tus cuatro fichas volverán a la base y el resto continuará. Si cierras la pestaña, tu asiento se conserva
              para que puedas reconectar; usar este botón es una retirada definitiva.
            </p>
            <div className="notice notice--warning"><Icon name="wifi" size={16} /> Si solo has perdido la conexión, espera unos segundos: el servidor te reconectará.</div>
          </div>
        )}
      </Modal>
    </div>
  );
}

interface TurnPanelProps {
  game: NonNullable<RoomView['game']>;
  currentName?: string;
  myTurn: boolean;
  myMoves: LegalMove[];
  busy: boolean;
  diceValue: number | null;
  activeDiceSkin: Preferences['diceSkin'];
  rollKey?: string;
  animations: boolean;
  astralDice: boolean;
  profileFrame: boolean;
  action: GameAction | null;
  myPlayerName: string;
  onRoll: () => void;
  onMove: (pieceId: string) => void;
}

function TurnPanel({
  game,
  currentName,
  myTurn,
  myMoves,
  busy,
  diceValue,
  activeDiceSkin,
  rollKey,
  animations,
  astralDice,
  profileFrame,
  action,
  myPlayerName,
  onRoll,
  onMove,
}: TurnPanelProps) {
  const current = game.players.find((player) => player.id === game.currentPlayerId);
  const isFinished = game.phase === 'finished';
  const waitingPass = myTurn && game.phase === 'awaitingMove' && myMoves.length === 0;
  const diceName = DICE_CATALOG.find((dice) => dice.id === activeDiceSkin)?.name ?? 'Obsidiana';

  return (
    <section className={`turn-panel ${myTurn ? 'is-mine' : ''}`}>
      <div className="turn-panel__top">
        <div className="turn-panel__player">
          {current && (
            <Avatar
              avatar={current.avatar}
              size={46}
              className={profileFrame && myTurn ? 'avatar--astral-frame' : ''}
            />
          )}
          <div>
            <span className="eyebrow">{isFinished ? 'Partida terminada' : myTurn ? 'Te toca' : 'Turno actual'}</span>
            <strong>{isFinished ? 'Fin del viaje' : currentName ?? '…'}</strong>
          </div>
        </div>
        <div className={`turn-panel__dice ${myTurn ? 'is-mine' : ''}`}>
          <Dice
            value={diceValue}
            skin={activeDiceSkin}
            rollKey={rollKey}
            animations={animations}
            size="small"
            effect={astralDice ? 'astral' : undefined}
          />
        </div>
      </div>

      {!isFinished && (
        <>
          {myTurn && game.phase === 'awaitingRoll' && (
            <div className="turn-panel__action">
              <p className="turn-panel__prompt">El servidor generará la tirada. Tú decides qué ficha mover.</p>
              <button type="button" className="button button--primary button--full button--large roll-button" onClick={onRoll} disabled={busy}>
                <Icon name="dice" size={20} /> {busy ? 'Tirando…' : 'Tirar el dado'}
              </button>
            </div>
          )}

          {myTurn && game.phase === 'awaitingMove' && myMoves.length > 0 && (
            <div className="turn-panel__action">
              <p className="turn-panel__prompt">
                Has sacado un <strong>{game.dice}</strong>. Elige una unidad o pulsa una de las opciones.
              </p>
              <div className="move-list">
                {myMoves.map((move) => {
                  const slot = game.players.find((player) => player.id === game.currentPlayerId)?.pieces.find((piece) => piece.id === move.pieceId)?.slot ?? 0;
                  return (
                    <button key={move.pieceId} type="button" className="move-button" onClick={() => onMove(move.pieceId)} disabled={busy}>
                      <span className="move-button__slot">{slot + 1}</span>
                      <span className="move-button__text">
                        <strong>{moveLabel(move, slot)}</strong>
                        <small>{moveDetails(move)}</small>
                      </span>
                      <Icon name="chevron-right" size={16} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {myTurn && waitingPass && (
            <div className="turn-panel__waiting">
              <span className="spinner spinner--small" />
              <p>Sin movimientos posibles. Pasando turno…</p>
            </div>
          )}

          {!myTurn && (
            <div className="turn-panel__waiting">
              <span className="waiting-dots"><i /><i /><i /></span>
              <p>Esperando a <strong>{currentName}</strong>…</p>
              <small>El servidor validará su tirada y su movimiento.</small>
            </div>
          )}
        </>
      )}

      {action?.message && <div className="last-action"><Icon name="spark" size={14} /> {action.message}</div>}

      <div className="turn-panel__footer">
        <span><Icon name="shield" size={13} /> {myPlayerName}</span>
        <span><Icon name="dice" size={13} /> {diceName}</span>
      </div>
    </section>
  );
}
